/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { FontSize, ROOT_FONT_SIZES, uiSizeOrder, STORAGE_KEYS } from '@/constants/config';

interface FontSizeContextType {
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
  cycleFontSize: () => void;
  rootSize: number;
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

export function FontSizeProvider({ children }: { children: ReactNode }) {
  const [fontSize, setFontSizeState] = useState<FontSize>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.fontSize);
    if (stored && (stored === 'compact' || stored === 'standard' || stored === 'comfortable')) {
      return stored;
    }
    return 'standard';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.fontSize, fontSize);

    // SET HTML FONT-SIZE - this is all that matters
    // All rem values in Tailwind will scale from this
    document.documentElement.style.fontSize = `${ROOT_FONT_SIZES[fontSize]}px`;
  }, [fontSize]);

  const setFontSize = (size: FontSize) => {
    setFontSizeState(size);
  };

  const cycleFontSize = () => {
    const currentIndex = uiSizeOrder.indexOf(fontSize);
    const nextIndex = (currentIndex + 1) % uiSizeOrder.length;
    setFontSizeState(uiSizeOrder[nextIndex]);
  };

  return (
    <FontSizeContext.Provider
      value={{
        fontSize,
        setFontSize,
        cycleFontSize,
        rootSize: ROOT_FONT_SIZES[fontSize],
      }}
    >
      {children}
    </FontSizeContext.Provider>
  );
}

export function useFontSize() {
  const context = useContext(FontSizeContext);
  if (context === undefined) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
}
